import jwtDecode from "jwt-decode"
import partial from "lodash/partial"
import { useAsyncFn } from "react-use"
import { getClient } from "../../../../../api/client"
import {
  CreatePasswordDocument,
  CreatePasswordMutation,
  CreatePasswordMutationVariables,
  UserFragmentDoc
} from "../../../../../api/generated"
import {
  AuthenticationTokenData,
  getAuthenticationToken
} from "../../../../../lib/utils/auth-token"
import { setPreferredAuthMethod } from "../../../utils/preferredAuthMethod"
import {
  passwordsFormDataSchema,
  passwordsSchema,
  refinePasswordsSchema
} from "../../change/api/action"

export const schema = refinePasswordsSchema(passwordsSchema)
export const formDataSchema = refinePasswordsSchema(passwordsFormDataSchema)

// We need to update the user's "no_password_set" field in the cache.
// after they create a password.
function updateCachedHasNoPassword() {
  const client = getClient()
  const tokenData = jwtDecode<AuthenticationTokenData>(
    getAuthenticationToken()!
  )
  const userId = tokenData.sub.split("user:")[1]
  const fragment = {
    id: `User:${userId}`,
    fragmentName: "User",
    fragment: UserFragmentDoc
  }
  const fragmentData = client.readFragment(fragment)
  client.writeFragment({
    ...fragment,
    data: {
      ...fragmentData,
      no_password_set: false
    }
  })
}

const createPasswordAction = async (
  formData: FormData
): Promise<{ success: true }> => {
  const client = getClient()
  let result = await client.mutate<
    CreatePasswordMutation,
    CreatePasswordMutationVariables
  >({
    mutation: CreatePasswordDocument,
    variables: formDataSchema.parse(formData)
  })

  const success = result.data?.createPassword

  if (!success) {
    throw new Error("Failed to create password.")
  }

  // NOTE: while this action results in a password change (see https://docs.djangoproject.com/en/4.2/topics/auth/default/#session-invalidation-on-password-change),
  // the backend will *not* invalidate the user's session in this case
  // so no need to re-authenticate the user.

  setPreferredAuthMethod("password")
  updateCachedHasNoPassword()

  return { success: true }
}

export const useCreatePasswordAction = partial(useAsyncFn, createPasswordAction)

export default createPasswordAction
